import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { ReportShell } from "@/features/reports/ReportShell";
import { useCalibrationDueList } from "@/features/calibration/api";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";
import type { ReportFilters } from "@/features/reports/types";

export const Route = createFileRoute("/_authenticated/reports/calibration-status")({
  component: CalibrationReport,
});

function CalibrationReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;
  const { data: rows = [], isLoading } = useCalibrationDueList({
    branchId: effectiveBranchId, departmentId: filters.departmentId, categoryId: filters.categoryId,
  });

  const exportRows = useMemo(() => () => rows.map((r) => ({
    Asset: r.asset_name, Code: r.asset_code, Department: r.department_name ?? "",
    Status: r.status, LastDone: r.last_done_date ?? "", NextDue: r.next_due_date ?? "",
  })), [rows]);


  return (
    <ReportShell id="R-06" title="Calibration Status & Due List"
      description="Current calibration status and upcoming dues."
      filters={filters} onChange={setFilters}
      enabled={["branchId", "departmentId", "categoryId"]} exportRows={exportRows}>
      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Asset</TableHead><TableHead>Code</TableHead><TableHead>Department</TableHead>
              <TableHead>Status</TableHead><TableHead>Last Done</TableHead><TableHead>Next Due</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.asset_id}>
                  <TableCell className="font-medium">{r.asset_name}</TableCell>
                  <TableCell className="font-mono text-xs">{r.asset_code}</TableCell>
                  <TableCell>{r.department_name ?? "—"}</TableCell>
                  <TableCell><Badge variant={r.status === "Overdue" ? "destructive" : r.status === "Due Soon" ? "secondary" : "outline"}>{r.status}</Badge></TableCell>
                  <TableCell>{r.last_done_date ?? "—"}</TableCell>
                  <TableCell>{r.next_due_date ?? "—"}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">No items.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </ReportShell>
  );
}
